'use client';

import type { ProductDisplayData as Product } from '@eliadmin/core/src/api/products/types/product-types';

import { Swiper, SwiperSlide } from 'swiper/react';

import 'swiper/css';
import 'swiper/css/pagination';

import { Navigation, Pagination } from 'swiper/modules';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { ProductCard } from './product-card';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchProducts } from '@eliadmin/core/src/api/products/product-api';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { buildProductsQuery } from '@eliadmin/core/src/utils/product-utils';
import { useQuery } from '@tanstack/react-query';

export const ProductsSwiper = ({
  initialProducts,
  categoryId,
  locale,
}: {
  initialProducts: Product[];
  categoryId: number;
  locale: string;
}) => {
  const { me, isInitialized } = useCustomerStore();
  const { data: products = initialProducts } = useQuery({
    queryKey: [
      CacheKey.PRODUCTS,
      'homepage-category',
      locale,
      categoryId,
      me?.customer.id ?? 'guest',
    ],
    queryFn: ({ signal }) =>
      fetchProducts({
        locale,
        authToken: me?.authToken,
        signal,
        query: {
          ...buildProductsQuery({ categories: [categoryId] }),
          results: 6,
          images: true,
          categories: true,
          attributes: true,
        },
      }).then(response => response.data),
    enabled: isInitialized,
    initialData: !me?.authToken ? initialProducts : undefined,
    placeholderData: initialProducts,
    staleTime: 5 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  return (
    <Swiper
      pagination={true}
      navigation={true}
      modules={[Pagination, Navigation]}
      className={cn('mySwiper', 'custom-container', 'ea-custom-swiper', 'product-swiper')}
      spaceBetween={16}
      slidesPerView={'auto'}
      breakpoints={{
        1280: {
          spaceBetween: 32,
        },
        1024: {
          spaceBetween: 24,
        },
        768: {
          spaceBetween: 20,
        },
      }}
    >
      {products.map(product => (
        <SwiperSlide
          key={product.slug}
          className="w-full min-w-[320px] max-w-[320px] md:min-w-[508px] md:max-w-[508px]"
        >
          <ProductCard product={product} key={product.slug} />
        </SwiperSlide>
      ))}
    </Swiper>
  );
};
